import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Clock, CheckCircle, XCircle, AlertTriangle, FileText, Download,
  MapPin, User, Calendar, ClipboardList, Car, PenTool, Eye,
  Filter, ChevronDown, ChevronRight, Loader2, Navigation
} from 'lucide-react';

// ADL Task names for display
const ADL_TASK_NAMES = {
  'meal_prep': 'Meal Preparation',
  'feeding': 'Feeding Assistance',
  'bathing': 'Bathing/Showering',
  'grooming': 'Grooming',
  'dressing': 'Dressing Assistance',
  'toileting': 'Toileting Assistance',
  'incontinence': 'Incontinence Care',
  'oral_care': 'Oral Hygiene',
  'mobility': 'Mobility Assistance',
  'transfer': 'Transfer Assistance',
  'ambulation': 'Ambulation/Walking',
  'exercise': 'Exercise/Range of Motion',
  'medication': 'Medication Reminder',
  'vital_signs': 'Vital Signs Check',
  'light_housekeeping': 'Light Housekeeping',
  'laundry': 'Laundry',
  'errands': 'Errands/Shopping',
  'companionship': 'Companionship',
  'supervision': 'Safety Supervision'
};

export default function ShiftReviewDashboard() {
  const [pendingShifts, setPendingShifts] = useState([]);
  const [approvedShifts, setApprovedShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedShift, setSelectedShift] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionNotes, setCorrectionNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Export filters
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchShifts();
  }, []);

  const fetchShifts = async () => {
    try {
      const [pendingRes, approvedRes] = await Promise.all([
        api.get('/shifts/admin/review/pending'),
        api.get('/shifts/admin/review/approved')
      ]);
      setPendingShifts(pendingRes.data);
      setApprovedShifts(approvedRes.data);
    } catch (error) {
      console.error('Failed to fetch shifts:', error);
      toast.error('Failed to load shifts for review');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (shiftId) => {
    setProcessing(true);
    try {
      await api.post(`/shifts/admin/review/${shiftId}/approve`);
      toast.success('Shift approved successfully');
      fetchShifts();
      setShowDetailModal(false);
    } catch (error) {
      toast.error('Failed to approve shift');
    } finally {
      setProcessing(false);
    }
  };

  const handleRequestCorrection = async () => {
    if (!correctionNotes.trim()) {
      toast.error('Please provide correction notes');
      return;
    }
    
    setProcessing(true);
    try {
      await api.post(`/shifts/admin/review/${selectedShift.id}/correction`, {
        notes: correctionNotes
      });
      toast.success('Correction request sent');
      setCorrectionNotes('');
      setShowCorrectionModal(false);
      setShowDetailModal(false);
      fetchShifts();
    } catch (error) {
      toast.error('Failed to request correction');
    } finally {
      setProcessing(false);
    }
  };

  const handleExportCSV = async () => {
    if (!exportStartDate || !exportEndDate) {
      toast.error('Please select a date range');
      return;
    }
    
    setExporting(true);
    try {
      const response = await api.get('/shifts/admin/export/quickbooks', {
        params: {
          start_date: exportStartDate,
          end_date: exportEndDate
        },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_export_${exportStartDate}_to_${exportEndDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const openDetailModal = (shift) => {
    setSelectedShift(shift);
    setShowDetailModal(true);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateDuration = (clockIn, clockOut) => {
    if (!clockIn || !clockOut) return '--';
    const start = new Date(clockIn);
    const end = new Date(clockOut);
    const hours = (end - start) / (1000 * 60 * 60);
    return hours.toFixed(2);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending_approval': 'bg-amber-100 text-amber-800',
      'approved': 'bg-green-100 text-green-800',
      'needs_correction': 'bg-red-100 text-red-800',
      'completed': 'bg-purple-100 text-purple-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  // Set default bi-weekly range
  useEffect(() => {
    const today = new Date();
    const twoWeeksAgo = new Date(today);
    twoWeeksAgo.setDate(today.getDate() - 14);
    
    setExportEndDate(today.toISOString().split('T')[0]);
    setExportStartDate(twoWeeksAgo.toISOString().split('T')[0]);
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="section-header mb-0">
          <h2 className="section-title">Shift Review Dashboard</h2>
          <p className="section-subtitle">Review and approve submitted shifts</p>
        </div>
      </div>

      {/* Tabs - Premium Style */}
      <div className="flex gap-2 border-b border-purple-100/60 pb-px">
        <button
          onClick={() => setActiveTab('pending')}
          className={`pb-3.5 px-4 font-medium transition-all duration-200 relative ${
            activeTab === 'pending'
              ? 'text-purple-900'
              : 'text-gray-500 hover:text-purple-700'
          }`}
        >
          Pending Approval
          {pendingShifts.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-amber-500 text-white rounded-full font-semibold shadow-sm">
              {pendingShifts.length}
            </span>
          )}
          {activeTab === 'pending' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
        </button>
        <button
          onClick={() => setActiveTab('approved')}
          className={`pb-3.5 px-4 font-medium transition-all duration-200 relative ${
            activeTab === 'approved'
              ? 'text-purple-900'
              : 'text-gray-500 hover:text-purple-700'
          }`}
        >
          Approved Shifts
          {activeTab === 'approved' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
        </button>
        <button
          onClick={() => setActiveTab('export')}
          className={`pb-3.5 px-4 font-medium transition-all duration-200 relative ${
            activeTab === 'export'
              ? 'text-purple-900'
              : 'text-gray-500 hover:text-purple-700'
          }`}
        >
          QuickBooks Export
          {activeTab === 'export' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
        </button>
      </div>

      {/* Pending Shifts Tab */}
      {activeTab === 'pending' && (
        <div className="space-y-4">
          {pendingShifts.length === 0 ? (
            <div className="card-premium p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-gray-500">No shifts pending approval</p>
            </div>
          ) : (
            pendingShifts.map(shift => (
              <div key={shift.id} className="card-premium p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-purple-900">{shift.title}</h3>
                      <span className={`px-2.5 py-0.5 text-xs rounded-full font-medium ${getStatusBadge(shift.status)}`}>
                        Pending Review
                      </span>
                      {shift.gps_verified && (
                        <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                          <Navigation className="w-3 h-3" />
                          GPS Verified
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-purple-400" />
                        <span>{shift.employee_name || 'Unknown'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-purple-400" />
                        <span>{formatDate(shift.shift_date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-purple-400" />
                        <span>{calculateDuration(shift.clock_in_time, shift.clock_out_time)} hrs</span>
                      </div>
                      {shift.client_name && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-purple-400" />
                          <span>{shift.client_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDetailModal(shift)}
                      className="hover:bg-purple-50"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Review
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleApprove(shift.id)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      disabled={processing}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Approved Shifts Tab */}
      {activeTab === 'approved' && (
        <div className="space-y-4">
          {approvedShifts.length === 0 ? (
            <div className="card-premium p-8 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No approved shifts yet</p>
            </div>
          ) : (
            <div className="card-premium overflow-hidden">
              <table className="table-premium">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Shift</th>
                    <th>Date</th>
                    <th>Duration</th>
                    <th>Mileage</th>
                    <th>Approved</th>
                  </tr>
                </thead>
                <tbody>
                  {approvedShifts.slice(0, 20).map(shift => (
                    <tr key={shift.id}>
                      <td>{shift.employee_name || '--'}</td>
                      <td className="font-medium text-purple-900">{shift.title}</td>
                      <td>{formatDate(shift.shift_date)}</td>
                      <td>{calculateDuration(shift.clock_in_time, shift.clock_out_time)} hrs</td>
                      <td>{shift.mileage || 0} mi</td>
                      <td>{formatDateTime(shift.approved_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* QuickBooks Export Tab */}
      {activeTab === 'export' && (
        <div className="card-premium p-6">
          <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
            <Download className="w-5 h-5" />
            QuickBooks Payroll Export
          </h3>
          
          <div className="space-y-4 max-w-md">
            <div>
              <Label className="text-gray-700 font-medium">Select Date Range (Bi-Weekly)</Label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <Label className="text-xs text-gray-400 font-medium">Start Date</Label>
                  <Input
                    type="date"
                    value={exportStartDate}
                    onChange={(e) => setExportStartDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-400 font-medium">End Date</Label>
                  <Input
                    type="date"
                    value={exportEndDate}
                    onChange={(e) => setExportEndDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50/50 rounded-xl p-4 border border-purple-100">
              <h4 className="font-medium text-purple-900 mb-2">CSV Format</h4>
              <p className="text-sm text-gray-600">
                The export will include: Date, Employee Name (Last, First), Service Item, 
                Duration (decimal), Mileage, and Notes.
              </p>
              <p className="text-xs text-gray-400 mt-2 font-medium">
                Dates formatted as MM/DD/YYYY for QuickBooks compatibility.
              </p>
            </div>
            
            <Button
              onClick={handleExportCSV}
              disabled={exporting || !exportStartDate || !exportEndDate}
              className="w-full btn-primary"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download QB Import File
            </Button>
          </div>
        </div>
      )}

      {/* Shift Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-purple-900">Shift Review</DialogTitle>
          </DialogHeader>
          
          {selectedShift && (
            <div className="space-y-6 mt-4">
              {/* Shift Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Employee</Label>
                  <p className="font-medium">{selectedShift.employee_name || '--'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Client</Label>
                  <p className="font-medium">{selectedShift.client_name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Shift Date</Label>
                  <p className="font-medium">{formatDate(selectedShift.shift_date)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Scheduled Time</Label>
                  <p className="font-medium">{selectedShift.start_time} - {selectedShift.end_time}</p>
                </div>
              </div>

              {/* Time & GPS Verification */}
              <Card className="p-4 bg-gray-50">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-purple-600" />
                  Time & Location
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-gray-500">Clock In</Label>
                    <p className="font-medium">{formatDateTime(selectedShift.clock_in_time)}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Clock Out</Label>
                    <p className="font-medium">{formatDateTime(selectedShift.clock_out_time)}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Total Duration</Label>
                    <p className="font-medium">{calculateDuration(selectedShift.clock_in_time, selectedShift.clock_out_time)} hours</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">GPS Status</Label>
                    <p className={`font-medium flex items-center gap-1 ${selectedShift.gps_verified ? 'text-green-600' : 'text-gray-500'}`}>
                      {selectedShift.gps_verified ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Verified at Client Location
                        </>
                      ) : (
                        'Not Verified'
                      )}
                    </p>
                  </div>
                </div>
              </Card>

              {/* ADL Tasks */}
              {selectedShift.adl_tasks && selectedShift.adl_tasks.length > 0 && (
                <Card className="p-4 bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-purple-600" />
                    Activities Completed ({selectedShift.adl_tasks.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedShift.adl_tasks.map(taskId => (
                      <span key={taskId} className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm">
                        {ADL_TASK_NAMES[taskId] || taskId}
                      </span>
                    ))}
                  </div>
                </Card>
              )}

              {/* Mileage & Notes */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-4 bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                    <Car className="w-4 h-4 text-purple-600" />
                    Mileage
                  </h4>
                  <p className="text-2xl font-bold text-purple-900">
                    {selectedShift.mileage || 0} miles
                  </p>
                </Card>
                
                {selectedShift.progress_notes && (
                  <Card className="p-4 bg-gray-50 col-span-2">
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-purple-600" />
                      Progress Notes
                    </h4>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {selectedShift.progress_notes}
                    </p>
                  </Card>
                )}
              </div>

              {/* Client Signature */}
              {selectedShift.client_signature_url && (
                <Card className="p-4 bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <PenTool className="w-4 h-4 text-purple-600" />
                    Client Signature
                  </h4>
                  <div className="bg-white border rounded-lg p-2 inline-block">
                    <img 
                      src={selectedShift.client_signature_url} 
                      alt="Client Signature" 
                      className="max-h-24"
                    />
                  </div>
                </Card>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCorrectionModal(true);
                  }}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Request Correction
                </Button>
                <Button
                  onClick={() => handleApprove(selectedShift.id)}
                  disabled={processing}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Approve Shift
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Correction Request Modal */}
      <Dialog open={showCorrectionModal} onOpenChange={setShowCorrectionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700">Request Correction</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Correction Notes</Label>
              <Textarea
                placeholder="Describe what needs to be corrected..."
                value={correctionNotes}
                onChange={(e) => setCorrectionNotes(e.target.value)}
                rows={4}
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCorrectionModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleRequestCorrection}
                disabled={processing || !correctionNotes.trim()}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2" />
                )}
                Send Correction Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
